import React, { FC, useEffect, useMemo, useRef, useState, memo } from 'react';
import ReactDOM from 'react-dom';
import {
	Box,
	Button,
	Flex,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Text,
	Checkbox,
} from 'src/components/ui';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@chakra-ui/icons';

export type TwoLevelChildItem = {
	label: string;
	value: string;
	disabled?: boolean;
};

export type TwoLevelParentItem = {
	label: string;
	value?: string; // Optional selection when parent has no children
	disabled?: boolean;
	children?: TwoLevelChildItem[];
};

export interface TwoLevelDropdownProps {
	items: TwoLevelParentItem[];
	onSelect: (item: TwoLevelChildItem | TwoLevelParentItem) => void;
	buttonLabel?: string;
	selectedValue?: string;
	selectedValues?: string[];
	multiSelect?: boolean;
	width?: string | number;
	maxHeight?: string | number;
}

const ITEM_HEIGHT = 36; // For positioning estimate; real value from padding/line height

const TwoLevelDropdown: FC<TwoLevelDropdownProps> = ({
	items,
	onSelect,
	buttonLabel = 'Select',
	selectedValue,
	selectedValues = [],
	multiSelect = false,
	width = 260,
	maxHeight = 280,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [openIndex, setOpenIndex] = useState<number | null>(null);
	const [submenuTop, setSubmenuTop] = useState(0);
	const [submenuLeft, setSubmenuLeft] = useState(0);
	const [submenuMaxHeight, setSubmenuMaxHeight] = useState(280);
	const [capturedChildren, setCapturedChildren] = useState<TwoLevelChildItem[]>(
		[],
	);

	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);
	const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
	const lastPointerPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const isPointerInsideList = useRef<boolean>(false);
	const submenuRef = useRef<HTMLDivElement | null>(null);

	const isItemSelected = (value: string) => {
		if (multiSelect) {
			return selectedValues.includes(value);
		}
		return selectedValue === value;
	};

	// Reset openIndex if it becomes invalid when items change
	useEffect(() => {
		if (openIndex !== null) {
			if (
				openIndex < 0 ||
				openIndex >= items.length ||
				!items[openIndex]?.children?.length
			) {
				setOpenIndex(null);
				setCapturedChildren([]);
			}
		}
	}, [items, openIndex]);

	const computeSubmenuPosition = (index: number) => {
		const parentEl = itemRefs.current[index];
		const wrapperEl = wrapperRef.current;
		if (!parentEl || !wrapperEl)
			return { top: index * ITEM_HEIGHT, left: 260, maxHeight: 280 };

		// Get absolute positions for portal positioning
		const parentRect = parentEl.getBoundingClientRect();
		const wrapperRect = wrapperEl.getBoundingClientRect();

		// Calculate available space below the parent element
		const viewportHeight = window.innerHeight;
		const spaceBelow = viewportHeight - parentRect.top;
		const maxSubmenuHeight = Math.min(
			spaceBelow - 20,
			parseInt(maxHeight.toString()) || 280,
		); // 20px padding from bottom

		return {
			top: parentRect.top,
			left: wrapperRect.right,
			maxHeight: Math.max(100, maxSubmenuHeight), // Minimum 100px height
		};
	};

	const handleParentHover = (index: number) => {
		// Only set openIndex if the item at this index has children
		if (index >= 0 && index < items.length && items[index]?.children?.length) {
			const currentItem = items[index];
			const children = currentItem.children || [];

			// Capture the children at this exact moment
			setCapturedChildren([...children]);
			setOpenIndex(index);
			const position = computeSubmenuPosition(index);
			setSubmenuTop(position.top);
			setSubmenuLeft(position.left);
			setSubmenuMaxHeight(position.maxHeight);
		} else {
			setOpenIndex(null);
			setCapturedChildren([]);
		}
	};

	const clearHover = () => {
		setOpenIndex(null);
		setCapturedChildren([]);
	};

	const handleSelectItem = (item: TwoLevelChildItem | TwoLevelParentItem) => {
		if (
			(item as TwoLevelParentItem).disabled ||
			(item as TwoLevelChildItem).disabled
		)
			return;

		// Call the onSelect callback
		onSelect(item);

		// In multi-select mode, keep both dropdown and submenu open for continuous selection
		if (multiSelect) {
			// Don't close anything - allow multiple selections
			return;
		} else {
			// In single-select mode, close everything
			setIsOpen(false);
			setOpenIndex(null);
			setCapturedChildren([]);
		}
	};

	// Recompute submenu position when scrolling the parent list
	useEffect(() => {
		const listEl = listRef.current;
		if (!listEl) return;
		const onScroll = () => {
			if (openIndex === null) return;

			// If pointer is inside the list, update hovered index based on pointer position
			if (isPointerInsideList.current) {
				const idx = getIndexFromPoint(
					lastPointerPos.current.x,
					lastPointerPos.current.y,
				);
				if (idx !== null && idx !== openIndex) {
					// Only update if we're hovering over a different item
					if (items[idx]?.children?.length) {
						const currentItem = items[idx];
						const children = currentItem.children || [];

						// Capture the children at this exact moment
						setCapturedChildren([...children]);
						setOpenIndex(idx);
						const position = computeSubmenuPosition(idx);
						setSubmenuTop(position.top);
						setSubmenuLeft(position.left);
						setSubmenuMaxHeight(position.maxHeight);
					} else {
						// Clear submenu if hovering over item without children
						setOpenIndex(null);
						setCapturedChildren([]);
					}
					return;
				}
			}

			// Check if current submenu item is still visible
			if (openIndex !== null) {
				const parentEl = itemRefs.current[openIndex];
				if (!parentEl) {
					setOpenIndex(null);
					return;
				}

				const visibleTop = parentEl.offsetTop - listEl.scrollTop;
				const visibleBottom = visibleTop + parentEl.offsetHeight;

				// Hide submenu if the parent item is completely out of view
				if (visibleBottom < 0 || visibleTop > listEl.clientHeight) {
					setOpenIndex(null);
					setCapturedChildren([]);
					return;
				}

				// Update submenu position for the current open item
				const position = computeSubmenuPosition(openIndex);
				setSubmenuTop(position.top);
				setSubmenuLeft(position.left);
				setSubmenuMaxHeight(position.maxHeight);
			}
		};
		listEl.addEventListener('scroll', onScroll, { passive: true });
		return () => listEl.removeEventListener('scroll', onScroll);
	}, [openIndex, items]);

	// Track pointer inside list to allow dynamic hover update during scroll
	useEffect(() => {
		const listEl = listRef.current;
		if (!listEl) return;
		const onMouseEnter = () => {
			isPointerInsideList.current = true;
		};
		const onMouseLeave = () => {
			isPointerInsideList.current = false;
		};
		const onMouseMove = (e: MouseEvent) => {
			lastPointerPos.current = { x: e.clientX, y: e.clientY };
		};
		listEl.addEventListener('mouseenter', onMouseEnter);
		listEl.addEventListener('mouseleave', onMouseLeave);
		listEl.addEventListener('mousemove', onMouseMove);
		return () => {
			listEl.removeEventListener('mouseenter', onMouseEnter);
			listEl.removeEventListener('mouseleave', onMouseLeave);
			listEl.removeEventListener('mousemove', onMouseMove);
		};
	}, []);

	// Handle clicks outside the dropdown and submenu
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			const isInsideWrapper = wrapperRef.current?.contains(target);
			const isInsideSubmenu = submenuRef.current?.contains(target);

			if (!isInsideWrapper && !isInsideSubmenu) {
				setIsOpen(false);
				setOpenIndex(null);
				setCapturedChildren([]);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const getIndexFromPoint = (
		clientX: number,
		clientY: number,
	): number | null => {
		const el = document.elementFromPoint(
			clientX,
			clientY,
		) as HTMLElement | null;
		if (!el) return null;
		const row = el.closest('[data-twolevel-index]') as HTMLElement | null;
		if (!row) return null;
		const idxAttr = row.getAttribute('data-twolevel-index');
		if (idxAttr == null) return null;
		const n = parseInt(idxAttr, 10);
		return Number.isNaN(n) ? null : n;
	};

	return (
		<Popover
			isOpen={isOpen}
			onOpen={() => setIsOpen(true)}
			onClose={() => {
				setIsOpen(false);
				setOpenIndex(null);
				setCapturedChildren([]);
			}}
			placement="bottom-start"
			offset={[0, 8]}
			closeOnBlur={false}
			closeOnEsc={true}
		>
			<PopoverTrigger>
				<Button
					variant="outline"
					bg={isOpen ? '#FFE8E0' : 'gray.50'}
					borderRadius="5px"
					borderColor={isOpen ? '#ffccbc' : 'gray.200'}
					color={isOpen ? '#F7480B' : 'inherit'}
					_hover={isOpen ? {} : { bg: 'gray.200', borderColor: 'gray.300' }}
					_active={{ bg: '#FFE8E0' }}
					rightIcon={<ChevronDownIcon />}
					fontFamily="Noto Sans"
					_focusVisible={{ outline: 'none' }}
					fontWeight="medium"
					fontSize="14px"
					height="40px"
					px={3}
					py={2}
					w="fit-content"
				>
					{buttonLabel}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				ref={wrapperRef}
				width={width}
				px={1.5}
				py={2}
				borderRadius="5px"
				bg="white"
				border="1px solid"
				borderColor="#E2E8F0"
				overflow="visible"
				position="relative"
				zIndex={99998}
				style={{ zIndex: 99998 }}
				onMouseLeave={clearHover}
			>
				<Box
					ref={listRef}
					maxH={maxHeight}
					overflowY="auto"
					overflowX="visible"
					pr={1}
				>
					{items.map((parent, index) => {
						const hasChildren = !!parent.children?.length;
						const isDisabled = !!parent.disabled;
						const isSelected = parent.value
							? isItemSelected(parent.value)
							: false;
						return (
							<Flex
								key={`${parent.label}-${index}`}
								ref={(el) => (itemRefs.current[index] = el)}
								data-twolevel-index={index}
								align="center"
								justify="space-between"
								px={3}
								py={2}
								gap={3}
								cursor={isDisabled ? 'not-allowed' : 'pointer'}
								opacity={isDisabled ? 0.5 : 1}
								_hover={{ bg: '#f9f9fa' }}
								role="menuitem"
								onMouseEnter={() => hasChildren && handleParentHover(index)}
								onClick={() =>
									!hasChildren && parent.value && handleSelectItem(parent)
								}
							>
								<Flex align="center" gap={2} minW={0} flex="1">
									{isSelected && <CheckIcon color="#2C6ECB" boxSize={3} />}
									<Text noOfLines={1} fontSize="14px">
										{parent.label}
									</Text>
								</Flex>
								{hasChildren && <ChevronRightIcon color="#2D3748" />}
							</Flex>
						);
					})}
				</Box>

				{openIndex !== null &&
					capturedChildren.length > 0 &&
					ReactDOM.createPortal(
						<Box
							ref={submenuRef}
							key={`submenu-${openIndex}-${items[openIndex]?.label}`}
							position="fixed"
							top={`${submenuTop}px`}
							left={`${submenuLeft}px`}
							// Slight overlap to avoid pointer gap when moving mouse
							bg="white"
							borderRadius="5px"
							boxShadow="0 0 0 1px rgba(0,0,0,.1), 0 4px 11px rgba(0,0,0,.1)"
							border="1px solid"
							borderColor="#E2E8F0"
							minW={width}
							zIndex={999999}
							onMouseLeave={(e) => {
								// Only clear hover if mouse is not moving to parent dropdown
								const rect = wrapperRef.current?.getBoundingClientRect();
								if (rect) {
									const { clientX, clientY } = e;
									const isMovingToParent =
										clientX >= rect.left &&
										clientX <= rect.right &&
										clientY >= rect.top &&
										clientY <= rect.bottom;
									if (!isMovingToParent) {
										clearHover();
									}
								} else {
									clearHover();
								}
							}}
						>
							<Box
								key={`children-${openIndex}-${items[openIndex]?.children?.length}`}
								maxH={`${submenuMaxHeight}px`}
								overflowY="auto"
								bg="white"
								onWheel={(e) => {
									e.stopPropagation();
								}}
							>
								{capturedChildren.map((child) => {
									const isSelected = isItemSelected(child.value);
									const isDisabled = !!child.disabled;
									return (
										<Flex
											key={child.value}
											align="center"
											justify="space-between"
											px={3}
											py={2}
											gap={3}
											cursor={isDisabled ? 'not-allowed' : 'pointer'}
											opacity={isDisabled ? 0.5 : 1}
											_hover={{ bg: '#f9f9fa' }}
											role="menuitem"
											onClick={() => handleSelectItem(child)}
										>
											<Flex align="center" gap={2} minW={0} flex="1">
												{multiSelect ? (
													<Checkbox
														isChecked={isSelected}
														size="sm"
														pointerEvents="none"
														sx={{
															'& .chakra-checkbox__control[data-checked]': {
																backgroundColor: 'orange.base',
																borderColor: 'orange.base',
																color: 'white',
															},
															'& .chakra-checkbox__control:not([data-checked])':
																{
																	borderColor: 'gray.300',
																},
															'& .chakra-checkbox__control:hover:not([data-checked])':
																{
																	borderColor: 'gray.400',
																},
														}}
													/>
												) : (
													isSelected && <CheckIcon boxSize={3} />
												)}
												<Text noOfLines={1} fontSize="14px">
													{child.label}
												</Text>
											</Flex>
										</Flex>
									);
								})}
							</Box>
						</Box>,
						document.body,
					)}
			</PopoverContent>
		</Popover>
	);
};

export default memo(TwoLevelDropdown);
